import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Panel } from "@/components/ui/panel";
import { PageSkeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import {
  FilterField,
  FilterToolbar,
  PaginationBar,
  SearchInput,
  formatUnix,
  selectClassName,
} from "./shared";

type AuditEvent = {
  id: string;
  accountId?: string | null;
  actorUserId?: string | null;
  action: string;
  resourceType: string;
  resourceId?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt: number;
};

export function SuperAdminAuditPage() {
  const [actor, setActor] = useState("");
  const [action, setAction] = useState("");
  const [accountId, setAccountId] = useState("");
  const [resourceType, setResourceType] = useState("");
  const [resourceId, setResourceId] = useState("");
  const [applied, setApplied] = useState({
    actor: "",
    action: "",
    accountId: "",
    resourceType: "",
    resourceId: "",
  });
  const [page, setPage] = useState(1);
  const limit = 50;
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (applied.actor) params.set("actor", applied.actor);
    if (applied.action) params.set("action", applied.action);
    if (applied.accountId) params.set("accountId", applied.accountId);
    if (applied.resourceType) params.set("resourceType", applied.resourceType);
    if (applied.resourceId) params.set("resourceId", applied.resourceId);
    void api<{ events: AuditEvent[]; total: number }>(
      `/api/platform/audit?${params}`,
    )
      .then((r) => {
        setEvents(r.events ?? []);
        setTotal(r.total ?? 0);
      })
      .catch(() => {
        setEvents([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [applied, page]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-rs-ink">Audit Log</h1>
        <p className="text-sm text-rs-ink-muted">
          Searchable platform audit with before/after metadata.
        </p>
      </div>
      <FilterToolbar>
        <FilterField
          id="sa-audit-actor"
          label="Actor User Id"
          className="min-w-[10rem] flex-1 space-y-1"
        >
          <SearchInput
            id="sa-audit-actor"
            value={actor}
            onChange={setActor}
            placeholder="User id"
          />
        </FilterField>
        <FilterField
          id="sa-audit-action"
          label="Action Prefix"
          className="min-w-[10rem] flex-1 space-y-1"
        >
          <SearchInput
            id="sa-audit-action"
            value={action}
            onChange={setAction}
            placeholder="e.g. platform."
          />
        </FilterField>
        <FilterField
          id="sa-audit-account"
          label="Account Id"
          className="min-w-[10rem] flex-1 space-y-1"
        >
          <SearchInput
            id="sa-audit-account"
            value={accountId}
            onChange={setAccountId}
            placeholder="Account id"
          />
        </FilterField>
        <FilterField
          id="sa-audit-rtype"
          label="Resource Type"
          className="min-w-[9rem] space-y-1"
        >
          <select
            id="sa-audit-rtype"
            className={selectClassName}
            value={resourceType}
            onChange={(e) => setResourceType(e.target.value)}
          >
            <option value="">Any</option>
            <option value="user">user</option>
            <option value="sync_stream">sync_stream</option>
            <option value="account_billing">account_billing</option>
            <option value="account_quotas">account_quotas</option>
            <option value="membership">membership</option>
          </select>
        </FilterField>
        <FilterField
          id="sa-audit-rid"
          label="Resource Id"
          className="min-w-[10rem] flex-1 space-y-1"
        >
          <Input
            id="sa-audit-rid"
            value={resourceId}
            onChange={(e) => setResourceId(e.target.value)}
            placeholder="Resource id"
          />
        </FilterField>
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            setApplied({
              actor: actor.trim(),
              action: action.trim(),
              accountId: accountId.trim(),
              resourceType: resourceType.trim(),
              resourceId: resourceId.trim(),
            });
            setPage(1);
          }}
        >
          Search
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            setActor("");
            setAction("");
            setAccountId("");
            setResourceType("");
            setResourceId("");
            setApplied({
              actor: "",
              action: "",
              accountId: "",
              resourceType: "",
              resourceId: "",
            });
            setPage(1);
          }}
        >
          Clear
        </Button>
      </FilterToolbar>

      {loading ? (
        <PageSkeleton />
      ) : (
        <>
          <Panel>
            <ul className="divide-y divide-rs-border">
              {events.map((e) => (
                <li key={e.id} className="px-5 py-3 text-sm">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="font-medium text-rs-ink">{e.action}</p>
                      <p className="text-rs-ink-muted">
                        {e.resourceType}
                        {e.resourceId ? ` · ${e.resourceId}` : ""}
                        {e.accountId ? ` · acct ${e.accountId}` : ""}
                        {e.actorUserId ? ` · actor ${e.actorUserId}` : ""}
                      </p>
                      <p className="text-xs text-rs-ink-subtle">
                        {formatUnix(e.createdAt)}
                      </p>
                    </div>
                    {e.metadata ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          setExpanded(expanded === e.id ? null : e.id)
                        }
                      >
                        {expanded === e.id ? "Hide Metadata" : "Show Metadata"}
                      </Button>
                    ) : null}
                  </div>
                  {expanded === e.id && e.metadata ? (
                    <pre className="mt-2 overflow-x-auto rounded-rs-sm bg-rs-muted p-3 text-xs text-rs-ink">
                      {JSON.stringify(e.metadata, null, 2)}
                    </pre>
                  ) : null}
                </li>
              ))}
              {events.length === 0 ? (
                <li className="px-5 py-8 text-center text-sm text-rs-ink-muted">
                  No audit events.
                </li>
              ) : null}
            </ul>
          </Panel>
          <PaginationBar
            page={page}
            limit={limit}
            total={total}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}
